import React, { useEffect, useMemo, useState } from "react"
import { Card, CardBody, Container, UncontrolledTooltip } from "reactstrap"
import Breadcrumb from "components/Common/Breadcrumb"
import TableContainer from "components/Common/TableContainer"
import { Link, useParams } from "react-router-dom"
import DefaultAvatar from "components/CommonForBoth/TopbarDropdown/DefaultAvatar"
import { useLazyGetInvestorsAgreementsByInvestorQuery } from "store/api/investors/investors.api"
import { usePermission } from "hooks/usePermission"
import { PERMISSIONS } from "helpers/roles"
import {
  formatAmount,
  getICDAgreementsStatus,
  getTableSortingHeaderIcon,
} from "helpers/common"
import eye from "../../assets/images/icons/eye.svg"
import downloadIcon from "../../assets/images/downloadGrey.svg"
import moment from "moment"

const defaultFilters = {
  page: 1,
  pageSize: 20,
}

const ViewInvestorInvestment = () => {
  const { id } = useParams()
  // const tabs = ["MASTER_AGREEMENT", "SCHEDULE_OF_TERMS"]
  const [activeTab, setActiveTab] = useState("MASTER_AGREEMENT")

  const [getResults, { isFetching }] =
    useLazyGetInvestorsAgreementsByInvestorQuery()
  const [data, setData] = useState([])
  const [count, setCount] = useState(0)
  const { hasPermission } = usePermission()

  //meta title
  document.title = "Agreements - Prachay"

  const getList = async () => {
    try {
      const { data } = await getResults({
        investor_id: id,
        doc_type:
          activeTab === "MASTER_AGREEMENT" ? "ICD_AGREEMENT" : "TERM_SHEET",
      }).unwrap()
      setData(data?.rows)
      setCount(data?.count)
    } catch (e) {}
  }

  useEffect(() => {
    getList()
  }, [id, activeTab])

  // useEffect(() => {
  //   const handleHashChange = () => {
  //     const hash = window.location.hash.replace("#", "").toUpperCase()
  //     if (tabs.includes(hash)) {
  //       setActiveTab(hash)
  //     } else {
  //       setActiveTab("PAYMENTS")
  //       window.location.hash = "MASTER_AGREEMENT"
  //     }
  //   }

  //   handleHashChange()
  //   window.addEventListener("hashchange", handleHashChange)

  //   return () => window.removeEventListener("hashchange", handleHashChange)
  // }, [])

  const columns = useMemo(() => {
    const data = [
      {
        header: ({ column }) => getTableSortingHeaderIcon(column, "Date"),
        id: "created_at",
        accessorFn: (row) =>
          row?.created_at ? new Date(row.created_at).getTime() : null,
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => row.original.formatted_created_at,
      },
      {
        header: ({ column }) => getTableSortingHeaderIcon(column, "Name"),
        accessorKey: "name",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <DefaultAvatar
            avatar_url={row?.original.investor?.avatar_url}
            email={row?.original.investor?.email}
            name={row?.original.investor?.name}
          />
        ),
      },
      ...(activeTab === "SCHEDULE_OF_TERMS"
        ? [
            {
              header: "Master Agreement Reference No.",
              accessorKey: "referenceNo",
              enableColumnFilter: false,
              enableSorting: true,
              cell: ({ row }) => (
                <>
                  {row?.original.agreement_ref.split("_")[0] ||
                    "Not Applicable"}
                </>
              ),
            },
          ]
        : []),
      {
        header:
          activeTab === "MASTER_AGREEMENT"
            ? "Reference No."
            : "Schedule Reference No",
        accessorKey: "referenceNo",
        enableColumnFilter: false,
        enableSorting: true,
        cell: ({ row }) => (
          <>{row?.original.agreement_ref || "Not Applicable"}</>
        ),
      },
      ...(activeTab === "MASTER_AGREEMENT"
        ? [
            {
              header: "Amount(₹)",
              accessorKey: "amount",
              enableColumnFilter: false,
              enableSorting: true,
              cell: ({ row }) => <>₹ {formatAmount(row?.original?.amount)}</>,
            },
            {
              header: "Remaining Amount(₹)",
              accessorKey: "remaining_amount",
              enableColumnFilter: false,
              enableSorting: true,
              cell: ({ row }) => (
                <>₹ {formatAmount(row?.original.remaining_amount)}</>
              ),
            },
            {
              header: "Expiration Date",
              accessorKey: "formatted_expiration_date",
              enableColumnFilter: false,
              enableSorting: true,
              cell: ({ row }) => <>{row?.original.formatted_expiration_date}</>,
            },
          ]
        : [
            {
              header: "Amount(₹)",
              accessorKey: "icdsRequests_amount",
              enableColumnFilter: false,
              enableSorting: true,
              cell: ({ row }) => (
                <>₹ {formatAmount(row?.original.icdsRequests?.amount)}</>
              ),
            },
            {
              header: "Interest Rate (%)",
              accessorKey: "icdsRequests_interest_rate",
              enableColumnFilter: false,
              enableSorting: true,
              cell: ({ row }) => (
                <>{row?.original.icdsRequests?.interest_rate} %</>
              ),
            },
            {
              header: "Tenure",
              accessorKey: "icdsRequests_tenure",
              enableColumnFilter: false,
              enableSorting: true,
              cell: ({ row }) => <>{row?.original.icdsRequests?.tenure} Days</>,
            },
            {
              header: ({ column }) =>
                getTableSortingHeaderIcon(column, "Date of Repayment"),
              accessorKey: "icdsRequests_repayment_date",
              enableColumnFilter: false,
              enableSorting: true,
              cell: ({ row }) => (
                <>
                  {moment(row?.original.icdsRequests?.repayment_date).format(
                    "D MMM YYYY"
                  )}
                </>
              ),
            },
          ]),
      {
        header: () => <div className="flex items-center gap-2">Status</div>,
        accessorKey: "status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="code-status">
            {getICDAgreementsStatus(row?.original?.status)}
          </div>
        ),
      },
      {
        header: "Audit Trail",
        id: "audit_trail",
        cell: ({ row }) => {
          const viewAuditTooltip = `viewAuditTooltip-${row?.original?.id}`
          return (
            <>
              {["SUCCESS"].includes(row?.original?.status) ? (
                <div className="d-flex justify-content-center align-items-center flex-row gap-2">
                  <div className="action-view">
                    <Link
                      to={row.original.audit_doc_url}
                      target="_blank"
                      id={viewAuditTooltip}
                    >
                      <img src={eye} alt="" />
                      <UncontrolledTooltip
                        placement="top"
                        target={viewAuditTooltip}
                      >
                        View Audit Trail
                      </UncontrolledTooltip>
                    </Link>
                  </div>
                </div>
              ) : (
                "Not Applicable"
              )}
            </>
          )
        },
      },

      {
        id: "actions",
        header: "Actions",
        cell: ({ row }) => {
          const viewTooltip = `viewTooltip-${row?.original?.id}`
          const downloadTooltip = `downloadTooltip-${row?.original?.id}`
          return (
            <>
              {["SUCCESS"].includes(row?.original?.status) ? (
                <div className="d-flex justify-content-center align-items-center flex-row gap-2">
                  {/* <a href={row.original.doc}>Sign Now</a> */}
                  <div className="action-view">
                    <Link
                      to={row.original.doc_url}
                      target="_blank"
                      id={viewTooltip}
                    >
                      <img src={eye} alt="" />
                      <UncontrolledTooltip placement="top" target={viewTooltip}>
                        View
                      </UncontrolledTooltip>
                    </Link>
                  </div>
                  <div className="action-view">
                    <Link
                      to={"#"}
                      id={downloadTooltip}
                      onClick={() =>
                        handleDownload(
                          row.original.doc_url,
                          row?.original.agreement_ref || "ICD Master Agreement"
                        )
                      }
                    >
                      <img src={downloadIcon} alt="" />
                      <UncontrolledTooltip
                        placement="top"
                        target={downloadTooltip}
                      >
                        Download
                      </UncontrolledTooltip>
                    </Link>
                  </div>
                </div>
              ) : (
                <></>
              )}
            </>
          )
        },
      },
    ]

    const canAccess = hasPermission(PERMISSIONS.ICD_VIEW)
    if (canAccess) return data
    return data.filter((item) => item.id !== "actions")
  }, [hasPermission])

  return (
    <React.Fragment>
      <div className="page-content mt-0">
        <Container fluid>
          <Breadcrumb title={"ICD Investment Details"} />
          <Card>
            <CardBody>
              <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center gap-2 justify-content-between">
                <div className="investment-trends-tab-section mb-3">
                  <div
                    onClick={() => setActiveTab("MASTER_AGREEMENT")}
                    className="tab-box"
                  >
                    <p
                      className={
                        activeTab === "MASTER_AGREEMENT" ? "active" : ""
                      }
                      style={{ minWidth: "150px" }}
                    >
                      Master Agreement
                    </p>
                  </div>
                  <div
                    onClick={() => setActiveTab("SCHEDULE_OF_TERMS")}
                    className="tab-box"
                  >
                    <p
                      className={
                        activeTab === "SCHEDULE_OF_TERMS" ? "active" : ""
                      }
                      style={{ minWidth: "150px" }}
                    >
                      Schedule of Terms
                    </p>
                  </div>
                </div>
              </div>
              <TableContainer
                noCardDesign={true}
                isLoading={isFetching}
                count={count}
                filters={defaultFilters}
                columns={columns}
                data={data}
                isGlobalFilter={true}
                isCustomPageSize={true}
                // isGlobalFilter={true}
                isPagination={true}
                pagination="pagination"
                paginationWrapper="dataTables_paginate paging_simple_numbers"
                tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
              />
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  )
}

export default ViewInvestorInvestment
